import {
  framingAnswerValue,
  parseFramingAnswer,
  SEGMENT_LABEL,
  sliceBaseline,
  storedAnswerScopeAt,
  type FramingAnswerRow,
  type FramingBaseline,
  type FramingNode,
  type FramingSegment,
} from "~/utils/demandFraming"

/**
 * The Cannibalization demand signal: one read-only setup percentage per stored Framing answer.
 * Pure, so the engine and the Framing view share the math.
 *
 * Each answer names a TARGET and `pct = target / Σ engine baseline − 1`. `none` targets the
 * covered variants' own baselines (signal 0), `full` the units the baseline's side sold, `partial`
 * a slider blend. The rate is sized on the bare baseline but applied AFTER the insights, so
 * `simulatedUnits`, `lyUnits` and `bySide` are baseline-only readings. Under `none` this diverges
 * from `simulateCannibalization`.
 *
 * A side answered `none` at season level under a season answer is an EXEMPTION: its variants keep
 * their baseline, and the season total still holds, carried by the remaining variants.
 *
 * Nothing is stored; ids start with {@link FRAMING_PREFIX} to tell them from `DemandAdjustment`
 * rows.
 */

/** The catalog id, and the `theme` the client resolves the signal's badge through. */
export const FRAMING_SIGNAL_THEME = "cannibalization"

const FRAMING_PREFIX = "framing:"

export const framingSignalId = (collectionId: number, n: FramingNode): string =>
  `${FRAMING_PREFIX}${FRAMING_SIGNAL_THEME}:${collectionId}:${n.segment}:${n.groupKey}:${n.groupValue}`

export const isFramingSignalKey = (key: string): boolean => key.startsWith(FRAMING_PREFIX)

/** The side a signal lands on: an exemption can leave a whole-season scope covering one side. */
const coveredSegment = (s: FramingSignal): FramingSegment => {
  if (s.scope.segment !== "") return s.scope.segment
  const sides = FRAMING_SIDES.filter((side) => s.bySide[side].variants > 0)
  return sides.length === 1 ? sides[0]! : ""
}

/** "Cannibalization · Carry-over[ · node]", named for the covered side, not the stored scope. */
export const framingSignalName = (s: FramingSignal): string =>
  [
    "Cannibalization",
    SEGMENT_LABEL[coveredSegment(s)],
    ...(s.scope.groupValue ? [s.scope.groupValue] : []),
  ].join(" · ")

/** `null` side = in no side (phased out, NOOS upcoming). */
export type FramingSide = Exclude<FramingSegment, "">

export type FramingVariantInput = {
  variantId: string
  side: FramingSide | null
  /** The node value on the signal level; `""` when it has none or there is no level. */
  groupValue: string
  /** `VariantTotals.baseline`, what the percentage folds on. */
  baseline: number
}

export type SideReading = {
  variants: number
  baselineUnits: number
  /** Baseline-only, before the insights. */
  simulatedUnits: number
}

export type FramingSignal = {
  id: string
  collectionId: number
  scope: FramingNode
  answer: string
  seasonVariants: number
  /** The target, on the baseline alone. */
  simulatedUnits: number
  /** The baseline reading's units; what `full` lands on. */
  lyUnits: number
  baselineUnits: number
  /** Covered variants a percentage cannot lift. */
  zeroBaselineVariants: number
  /** Target shared out by each side's share of `baselineUnits`; both sides always present. */
  bySide: Record<FramingSide, SideReading>
  /** `simulatedUnits / baselineUnits − 1`, clamped at −100; `null` with no baseline to lift. */
  pct: number | null
  variantIds: string[]
}

const QUESTION = "cannibalization" as const

const EMPTY_BASELINE: FramingBaseline = {
  variants: 0,
  newVariants: 0,
  carryOverVariants: 0,
  newUnits: 0,
  carryOverUnits: 0,
}

const FRAMING_SIDES: readonly FramingSide[] = ["new", "carryOver"]

/** Same split the one percentage performs when the engine folds it per variant. */
const readSides = (
  covered: readonly FramingVariantInput[],
  baselineUnits: number,
  target: number,
): Record<FramingSide, SideReading> => {
  const out = {
    new: { variants: 0, baselineUnits: 0, simulatedUnits: 0 },
    carryOver: { variants: 0, baselineUnits: 0, simulatedUnits: 0 },
  }
  for (const v of covered) {
    if (v.side == null) continue
    out[v.side].variants += 1
    out[v.side].baselineUnits += Math.max(0, v.baseline)
  }
  for (const side of FRAMING_SIDES)
    out[side].simulatedUnits =
      baselineUnits > 0 ? (target * out[side].baselineUnits) / baselineUnits : 0
  return out
}

/** A side answered `none` at season level: see the module comment. */
const isExemption = (r: FramingAnswerRow): boolean =>
  r.segment !== "" && r.groupKey === "" && parseFramingAnswer(r.answer)?.optionId === "none"

const unitsOf = (b: FramingBaseline): number => b.newUnits + b.carryOverUnits

/** `null` for an option that names no target. */
const signalTarget = (ownUnits: number, lyUnits: number, answer: string): number | null => {
  const option = parseFramingAnswer(answer)?.optionId
  switch (option) {
    case "none":
      return ownUnits
    case "full":
      return lyUnits
    case "partial": {
      const absorbed = (framingAnswerValue(QUESTION, answer) ?? 50) / 100
      return lyUnits + (ownUnits - lyUnits) * (1 - absorbed)
    }
    default:
      return null
  }
}

/**
 * One signal per stored answer covering at least one variant this season. Each variant follows its
 * nearest stored answer (`storedAnswerScopeAt`); the baseline reading is cut into (side, node)
 * CELLS that follow the same chain, so each scope's percentage answers for exactly its own
 * variants. An exemption's cells stay in the reading of the scope they REACH (resolved with
 * exemptions lifted), whose target then drops their own baselines. `signalLevel` is the one level
 * whose node answers apply.
 */
export const framingSignals = (
  collectionId: number,
  answers: readonly FramingAnswerRow[],
  baseline: { overall: FramingBaseline; nodes: Record<string, FramingBaseline> },
  signalLevel: string,
  variants: readonly FramingVariantInput[],
): FramingSignal[] => {
  const rows = answers.filter(
    (r) => r.question === QUESTION && (r.groupKey === "" || r.groupKey === signalLevel),
  )
  if (rows.length === 0) return []

  const unexempt = rows.filter((r) => !isExemption(r))

  // No value on the level, or no level: the cell sits on the side itself.
  const cellNode = (side: FramingSide, groupValue: string): FramingNode =>
    signalLevel !== "" && groupValue !== ""
      ? { segment: side, groupKey: signalLevel, groupValue }
      : { segment: side, groupKey: "", groupValue: "" }
  const scopeOfCell = (side: FramingSide, groupValue: string) =>
    storedAnswerScopeAt(rows, QUESTION, cellNode(side, groupValue))
  const reachOfCell = (side: FramingSide, groupValue: string) =>
    storedAnswerScopeAt(unexempt, QUESTION, cellNode(side, groupValue))

  const coveredBy = new Map<FramingAnswerRow, FramingVariantInput[]>()
  // Σ own baseline of the exempted variants each scope keeps in its target.
  const exemptedBaseline = new Map<FramingAnswerRow, number>()
  for (const v of variants) {
    if (v.side == null) continue
    const scope = scopeOfCell(v.side, v.groupValue)
    if (!scope) continue
    const list = coveredBy.get(scope)
    if (list) list.push(v)
    else coveredBy.set(scope, [v])
    if (isExemption(scope)) {
      const reach = reachOfCell(v.side, v.groupValue)
      if (reach)
        exemptedBaseline.set(reach, (exemptedBaseline.get(reach) ?? 0) + Math.max(0, v.baseline))
    }
  }

  // An exempted cell is summed onto both its reach and the exemption itself.
  const groupValues =
    signalLevel === ""
      ? [""]
      : [...new Set([...Object.keys(baseline.nodes), ...variants.map((v) => v.groupValue)])]
  const readingByScope = new Map<FramingAnswerRow, { variants: number; units: number }>()
  const addReading = (scope: FramingAnswerRow, cell: FramingBaseline) => {
    const acc = readingByScope.get(scope) ?? { variants: 0, units: 0 }
    acc.variants += cell.variants
    acc.units += unitsOf(cell)
    readingByScope.set(scope, acc)
  }
  for (const side of FRAMING_SIDES) {
    for (const groupValue of groupValues) {
      const cell = sliceBaseline(
        signalLevel === "" ? baseline.overall : (baseline.nodes[groupValue] ?? EMPTY_BASELINE),
        side,
      )
      const reach = reachOfCell(side, groupValue)
      if (reach) addReading(reach, cell)
      const scope = scopeOfCell(side, groupValue)
      if (scope && isExemption(scope)) addReading(scope, cell)
    }
  }

  const out: FramingSignal[] = []
  for (const [scope, covered] of coveredBy) {
    const reading = readingByScope.get(scope) ?? { variants: 0, units: 0 }
    const readingUnits = reading.units
    const baselineUnits = covered.reduce((s, v) => s + Math.max(0, v.baseline), 0)
    const zeroBaselineVariants = covered.filter((v) => v.baseline <= 0).length
    // Same guard as the card: nothing in the reading to cannibalize.
    if (reading.variants <= 0) continue
    const exempted = exemptedBaseline.get(scope) ?? 0
    const whole = signalTarget(baselineUnits + exempted, readingUnits, scope.answer)
    if (whole == null) continue
    const target = Math.max(0, whole - exempted)

    out.push({
      id: framingSignalId(collectionId, scope),
      collectionId,
      scope: { segment: scope.segment, groupKey: scope.groupKey, groupValue: scope.groupValue },
      answer: scope.answer,
      seasonVariants: covered.length,
      simulatedUnits: target,
      lyUnits: readingUnits,
      baselineUnits,
      zeroBaselineVariants,
      bySide: readSides(covered, baselineUnits, target),
      pct: baselineUnits > 0 ? Math.max(-100, (target / baselineUnits - 1) * 100) : null,
      variantIds: covered.map((v) => v.variantId).sort(),
    })
  }
  return out.sort(
    (a, b) =>
      a.scope.segment.localeCompare(b.scope.segment) ||
      a.scope.groupKey.localeCompare(b.scope.groupKey) ||
      a.scope.groupValue.localeCompare(b.scope.groupValue),
  )
}

/** Per-variant rows the engine folds, from the signals that carry a percentage. */
export const framingRowsByVariant = (
  signals: readonly FramingSignal[],
): Record<string, { id: string; value: number }[]> => {
  const out: Record<string, { id: string; value: number }[]> = {}
  for (const s of signals) {
    if (s.pct == null) continue
    for (const variantId of s.variantIds) (out[variantId] ??= []).push({ id: s.id, value: s.pct })
  }
  return out
}
